import { ChevronDown, FileText, FileWarning } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import { OptroWorkpaperFile } from "../../../../../models/audit/optroSync";

// The controls review step's workpaper cell. An empty cell means the agentic
// match job searched Optro and found nothing, so it reads as a finding worth
// flagging. Work steps have no matcher and default to every Optro file on the
// item, so they render their own `WorkStepWorkpaperChip` instead.
interface WorkpaperChipProps {
  workpapers: OptroWorkpaperFile[];
  onChange: () => void; // open the Change Workpaper dialog
  onRemove: () => void;
  // The match job never produced an answer for this control, so the empty
  // state must not read "None found" — that asserts we looked, and is
  // indistinguishable from a control that genuinely has no workpaper on file.
  // Only changes the label; see `isMatchFailed` in `OptroImportReviewPage` for
  // what counts as failed and for why nothing else about the row changes.
  matchFailed: boolean;
}

export const WorkpaperChip = ({
  workpapers,
  onChange,
  onRemove,
  matchFailed,
}: WorkpaperChipProps) => {
  if (workpapers.length === 0) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger
          className={`inline-flex items-center gap-1.5 text-sm ${
            matchFailed ? "text-red-600" : "text-amber-600"
          }`}
        >
          <FileWarning className="h-4 w-4" />
          {matchFailed ? "Failed" : "None found"}
          <ChevronDown className="h-3.5 w-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuItem onClick={onChange}>Add workpaper</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="inline-flex items-center gap-1.5 text-sm">
        <FileText className="h-4 w-4 text-stone-500" />
        {workpapers.length === 1
          ? workpapers[0].fileName
          : `${workpapers[0].fileName} +${workpapers.length - 1}`}
        <ChevronDown className="h-3.5 w-3.5 text-stone-400" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={onChange}>Replace</DropdownMenuItem>
        <DropdownMenuItem onClick={onRemove}>Remove</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
